import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { MapPin } from 'lucide-react'
import { useWizardStepConfig } from './SetupWizardLayout'
import {
  markStepCompleted,
  markStepSkipped,
  readDraft,
  writeDraft,
} from './signupState'
import VenueAutocomplete from './VenueAutocomplete'
import VenueCard from './VenueCard'
import { persistWizardVenuesToCommunityLocations } from '../../lib/persistCommunityLocations'

/**
 * SetupWizardLocations — Wizard Step 1.
 *
 * Google Places venue search via src/lib/venuesApi.js, restricted to
 * draft.country when set. Each
 * confirmed pick stacks below as a card. Manual fallback ("Can't find it?") creates an entry with
 * isManual: true so the backend can later choose whether to enrich it
 * via geocoding or surface it for the sales team to chase down.
 *
 * Draft venues live in sessionStorage (signupState.js) and sync to
 * community_locations when the community exists (continue/skip + policies finish).
 * A refresh
 * mid-flow restores the venues array verbatim.
 */

const STEP = 'locations'
const NEXT_ROUTE = '/signup/setup/size'

function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function makeVenueFromPlace(place) {
  return {
    placeId: place.placeId,
    name: place.name,
    formattedAddress: place.formattedAddress,
    lat: place.lat,
    lng: place.lng,
    countryCode: place.countryCode,
    photoUrl: place.photoUrl,
    rating: place.rating,
    ratingCount: place.ratingCount,
    instagramHandle: null,
    isManual: false,
  }
}

function makeManualVenue(name) {
  return {
    placeId: `manual_${uuid()}`,
    name: name.trim(),
    formattedAddress: '',
    lat: null,
    lng: null,
    countryCode: '',
    photoUrl: null,
    rating: null,
    ratingCount: null,
    instagramHandle: null,
    isManual: true,
  }
}

export default function SetupWizardLocations() {
  const navigate = useNavigate()

  const initialDraft = useMemo(readDraft, [])
  const countryCode = initialDraft.country || null

  const [venues, setVenues] = useState(
    () => initialDraft.setupWizard?.venues || [],
  )

  const [pulseTokens, setPulseTokens] = useState({})
  const pulseTimers = useRef({})

  const persistVenues = useCallback((next) => {
    const wizard = readDraft().setupWizard
    writeDraft({ setupWizard: { ...wizard, venues: next } })
  }, [])

  const selectVenue = useCallback(
    (place) => {
      setVenues((prev) => {
        const existing = prev.find((v) => v.placeId === place.placeId)
        if (existing) {
          setPulseTokens((tokens) => ({
            ...tokens,
            [place.placeId]: (tokens[place.placeId] || 0) + 1,
          }))
          if (pulseTimers.current[place.placeId]) {
            clearTimeout(pulseTimers.current[place.placeId])
          }
          pulseTimers.current[place.placeId] = setTimeout(() => {
            setPulseTokens((tokens) => {
              const { [place.placeId]: _drop, ...rest } = tokens
              return rest
            })
          }, 250)
          return prev
        }
        const next = [...prev, makeVenueFromPlace(place)]
        persistVenues(next)
        return next
      })
    },
    [persistVenues],
  )

  const addManualVenue = useCallback(
    (rawName) => {
      const name = rawName.trim()
      if (!name) return
      setVenues((prev) => {
        const dup = prev.find(
          (v) => v.isManual && v.name.toLowerCase() === name.toLowerCase(),
        )
        if (dup) {
          setPulseTokens((tokens) => ({
            ...tokens,
            [dup.placeId]: (tokens[dup.placeId] || 0) + 1,
          }))
          if (pulseTimers.current[dup.placeId]) {
            clearTimeout(pulseTimers.current[dup.placeId])
          }
          pulseTimers.current[dup.placeId] = setTimeout(() => {
            setPulseTokens((tokens) => {
              const { [dup.placeId]: _drop, ...rest } = tokens
              return rest
            })
          }, 250)
          return prev
        }
        const next = [...prev, makeManualVenue(name)]
        persistVenues(next)
        return next
      })
    },
    [persistVenues],
  )

  const removeVenue = useCallback(
    (placeId) => {
      setVenues((prev) => {
        const next = prev.filter((v) => v.placeId !== placeId)
        persistVenues(next)
        return next
      })
    },
    [persistVenues],
  )

  const existingPlaceIds = useMemo(
    () => new Set(venues.map((v) => v.placeId)),
    [venues],
  )

  const syncVenuesToDatabase = useCallback(async () => {
    const draft = readDraft()
    if (!draft.communityId) return
    const list = draft.setupWizard?.venues || []
    if (list.length === 0) return
    await persistWizardVenuesToCommunityLocations(draft.communityId, list)
  }, [])

  const handleContinue = useCallback(async () => {
    await syncVenuesToDatabase()
    markStepCompleted(STEP)
    navigate(NEXT_ROUTE)
  }, [navigate, syncVenuesToDatabase])

  const handleSkip = useCallback(async () => {
    await syncVenuesToDatabase()
    markStepSkipped(STEP)
    navigate(NEXT_ROUTE)
  }, [navigate, syncVenuesToDatabase])

  useWizardStepConfig({
    canContinue: true,
    onContinue: handleContinue,
    onSkipStep: handleSkip,
  })

  return (
    <>
      <h1
        className="font-medium text-[var(--signup-text-primary)] text-[30px] sm:text-[36px] leading-[1.1]"
        style={{ letterSpacing: '-0.01em' }}
      >
        Where does your community play?
      </h1>
      <p className="mt-4 text-[15px] text-[var(--signup-text-secondary)] leading-[1.55]">
        Add the venues you usually use. We'll surface games at these
        locations.
      </p>

      <div
        className="mt-8 rounded-xl border border-[var(--signup-border-subtle)] bg-[var(--signup-bg-elevated)] p-4"
        style={{ boxShadow: 'var(--signup-shadow-sm)' }}
      >
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[var(--signup-accent)]/25 bg-[var(--signup-accent-subtle)] px-3 py-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--signup-accent)] text-white">
            <MapPin size={16} strokeWidth={2.25} aria-hidden />
          </span>
          <span className="text-[14px] font-medium text-[var(--signup-text-primary)] leading-tight">
            Powered by Google Maps
          </span>
        </div>

        <VenueAutocomplete
          countryCode={countryCode}
          onSelect={selectVenue}
          onAddManual={addManualVenue}
          existingPlaceIds={existingPlaceIds}
          embedded
          autoFocus
        />
      </div>

      <div className="mt-4">
        {venues.length === 0 ? (
          <div className="flex items-start gap-3" aria-live="polite">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--signup-accent-subtle)] text-[var(--signup-accent)]">
              <MapPin size={16} strokeWidth={2.25} aria-hidden />
            </span>
            <p className="pt-1.5 text-[13px] italic text-[var(--signup-text-tertiary)] leading-snug">
              No venues added yet. You can skip this step and add later.
            </p>
          </div>
        ) : (
          <div
            role="list"
            aria-label="Added venues"
            className="flex flex-col gap-3"
          >
            <AnimatePresence initial={false}>
              {venues.map((venue) => (
                <VenueCard
                  key={venue.placeId}
                  venue={venue}
                  onRemove={() => removeVenue(venue.placeId)}
                  pulseToken={pulseTokens[venue.placeId] || 0}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </>
  )
}
